<template>
  <div class="product-item-option">
    <!-- Multiple items view (collapsible) -->
    <div v-if="items.length > 1" class="multiple-items-view">
      <div class="product-header" :class="{ expanded: isExpanded }" @click="toggleExpanded">
        <div class="product-content">
          <div class="product-info">
            <div class="product-title" role="heading" aria-level="2">
              {{ name }}
            </div>
            <div class="product-price" :aria-label="`${tStatic('fromPriceLabel')} ${formatPrice(price, currency, region)}`">
              <span><b>{{ tStatic('fromPriceLabel') }} {{ formatPrice(price, currency, region) }}</b></span>
              <span class="price-vat">{{ vatIncluded ? tStatic('priceIncludingVat') : tStatic('priceExcludingVat') }}</span>
            </div>
          </div>
          <div class="expand-icon" :class="{ rotated: isExpanded }">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </div>
        </div>
      </div>

      <transition name="slide">
        <div v-if="isExpanded" class="items-container">
          <div v-for="item in items" :key="item.partNumber" class="item-option">
            <a
              :href="item.contentUrl"
              :aria-label="`Item info for ${item.name}`"
              @click="handleItemClick(item, $event)">
              <div class="item-content">
                <div class="item-main-info">
                  <div class="item-secondary-info">
                    <a
                      :href="item.contentUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="item-number-link"
                      :aria-label="`Item number ${item.itemNumber} - opens in new tab`"
                      @click.stop="handleItemNumberClick(item, $event)">
                      <span class="item-number">{{ item.partNumber }}</span>
                    </a>
                  </div>
                  <div class="item-bottom-row">
                    <div v-if="item.isExpiredItem" class="expired-item">
                      <oie-base-icon icon="warning"></oie-base-icon>
                      <span v-html="tStatic('expiredItemContent')"></span>
                    </div>
                    <div v-else-if="item.isQuoteItem" class="quoted-item">
                      <div v-html="tStatic('requestQuoteContent')"></div>
                    </div>
                    <template v-else>
                      <div class="item-stock">
                        <oie-product-status
                          v-if="item.stockMessage && item.stockStatusCss"
                          :status="item.stockStatusCss"
                          :text="item.stockMessage">
                        </oie-product-status>
                      </div>
                      <div class="item-price" :aria-label="`Item price ${formatPrice(item.price, currency, region)}`">
                        <span><b>{{ formatPrice(item.price, currency, region) }}</b></span>
                        <span class="price-vat">{{ vatIncluded ? tStatic('priceIncludingVat') : tStatic('priceExcludingVat') }}</span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </transition>
    </div>

    <!-- Single item view (non-collapsible) -->
    <div v-else-if="items.length === 1" class="single-item-view">
      <div class="single-item-content">
        <a
          :href="items[0].contentUrl"
          :aria-label="`Item info for ${items[0].name}`"
          @click="handleItemClick(items[0], $event)">
          <div class="single-item-details">
            <div class="single-item-main-info">
              <div class="single-item-title">{{ items[0].name }}</div>
              <div class="single-item-secondary-info">
                <a
                  :href="items[0].contentUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="single-item-number-link"
                  :aria-label="`Item number ${items[0].itemNumber} - opens in new tab`"
                  @click.stop="handleItemNumberClick(items[0], $event)">
                  <span class="single-item-number">{{ items[0].partNumber }}</span>
                </a>
              </div>
              <div class="single-item-bottom-row">
                <div v-if="items[0].isExpiredItem" class="expired-item">
                  <oie-base-icon icon="warning"></oie-base-icon>
                  <span v-html="tStatic('expiredItemContent')"></span>
                </div>
                <div v-else-if="items[0].isQuoteItem" class="quoted-item">
                  <div v-html="tStatic('requestQuoteContent')"></div>
                </div>
                <template v-else>
                  <div class="single-item-stock">
                    <oie-product-status
                      v-if="items[0].stockMessage && items[0].stockStatusCss"
                      :status="items[0].stockStatusCss"
                      :text="items[0].stockMessage">
                    </oie-product-status>
                  </div>
                  <div class="single-item-price" :aria-label="`Item price ${formatPrice(items[0].price, currency, region)}`">
                    <span><b>{{ formatPrice(items[0].price, currency, region) }}</b></span>
                    <span class="single-price-vat">{{ vatIncluded ? tStatic('priceIncludingVat') : tStatic('priceExcludingVat') }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import gtm from '@/core/enhanced-ecommerce-gtm'

export default {
  props: {
    sessionId: {
      type: String,
      required: true
    },
    exchangeId: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    price: {
      type: Number,
      default: 0
    },
    currency: {
      type: String,
      default: null
    },
    region: {
      type: String,
      default: null
    },
    vatIncluded: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isExpanded: false
    }
  },
  methods: {
    formatPrice(price, currency, region) {
      if(!region || !currency) {
        console.log('currency or region not returned.')
        return price
      }
      return new Intl.NumberFormat(region, { style: 'currency', currency, maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(price)
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded
    },
    handleItemClick(item, event) {
      event.preventDefault()
      const separator = item.contentUrl.includes('?') ? '&' : '?'
      const trackingUrl = `${item.contentUrl}${separator}chatSession=${this.sessionId}&exchangeId=${this.exchangeId}`

      localStorage.setItem('chat_conversion', JSON.stringify({ chatSessionId: this.sessionId }))
      gtm.chatProductClick(this.sessionId, item.itemNumber)
      window.open(trackingUrl, '_self')
    },
    handleItemNumberClick(item, event) {
      event.preventDefault()
      event.stopPropagation()
      const separator = item.contentUrl.includes('?') ? '&' : '?'
      const trackingUrl = `${item.contentUrl}${separator}chatSession=${this.sessionId}&exchangeId=${this.exchangeId}`

      localStorage.setItem('chat_conversion', JSON.stringify({ chatSessionId: this.sessionId }))
      gtm.chatProductClick(this.sessionId, item.itemNumber)
      window.open(trackingUrl, '_self')
    }
  }
}
</script>

<style scoped lang="scss">
.product-item-option {
  background-color: #fff;
  border: 1px solid #ccc;
  margin-bottom: 0.625rem;
  border-radius: 4px;
  overflow: hidden;
}

.product-header {
  padding: 0.75rem;
  cursor: pointer;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e9ecef;
  }

  &.expanded {
    background-color: #e3f2fd;
  }
}

.product-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.product-info {
  flex: 1;
}

.product-title {
  font-weight: bold;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  color: #333;
}

.product-price {
  font-size: 0.9rem;
  color: #666;

  b {
    color: #006db6;
    font-size: 0.8rem;
  }

  .price-vat {
    display: block;
    font-size: 0.7rem;
    color: #006db6;
    margin-top: 0.1rem;
  }
}

.expand-icon {
  transition: transform 0.3s ease;
  color: #666;

  &.rotated {
    transform: rotate(180deg);
  }
}

.items-container {
  background-color: #fff;
}

.item-option {
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }

  a {
    display: block;
    padding: 0.4rem 1rem;
    color: #000;
    text-decoration: none;
    width: 100%;
  }
}

.item-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.item-main-info {
  flex: 1;
}

.item-secondary-info {
  margin-bottom: 0.25rem;
}

.item-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.item-number {
  font-size: 0.75rem;
  color: #006db6;
  font-family: monospace;
  font-weight: bold;
}

.item-number-link {
  text-decoration: none;
  display: inline-block;
  padding: 0 !important;

  &:hover .item-number {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #006db6;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.item-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.item-stock {
  font-size: 0.75rem;
}

.item-price {
  text-align: right;

  b {
    color: #006db6;
    font-size: 0.8rem;
  }

  .price-vat {
    font-size: 0.7rem;
    color: #006db6;
    margin-top: 0.1rem;
    margin-left: 0.4rem;
  }
}

.expired-item {
  background-color: #DFDFDF;
  border: 1px solid #757272;
  font-size: 0.7rem;
  padding: 2px 4px;
  display: flex;
  gap: 5px;
}

.quoted-item{
  padding: 0.3rem;
  font-size: 0.75rem;
  background-color: #f7efe1;
  border: 1px solid #9d6917;
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.slide-enter-from {
  max-height: 0;
  opacity: 0;
}

.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 500px;
  opacity: 1;
}

// Responsive design
@media (max-width: 768px) {
  .item-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .item-bottom-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    width: 100%;
  }

  .item-price {
    text-align: right;
  }

  .single-item-bottom-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    width: 100%;
  }

  .single-item-price {
    text-align: right;
  }
}

// Single item view styles
.single-item-view {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.single-item-content {
  a {
    display: block;
    padding: 0.6rem 0.6rem;
    color: #000;
    text-decoration: none;
    width: 100%;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}

.single-item-details {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.single-item-main-info {
  flex: 1;
}

.single-item-title {
  font-weight: bold;
  font-size: 0.8rem;
  color: #333;
}

.single-item-secondary-info {
  margin-bottom: 0.4rem;
}

.single-item-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.single-item-number {
  font-size: 0.7rem;
  color: #006db6;
  font-family: monospace;
  font-weight: bold;
}

.single-item-number-link {
  text-decoration: none;
  display: inline-block;
  padding: 0 !important;

  &:hover .single-item-number {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #006db6;
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.single-item-stock {
  font-size: 0.75rem;
}

.single-item-price {
  text-align: right;

  b {
    color: #006db6;
    font-size: 0.8rem;
  }

  .single-price-vat {
    font-size: 0.7rem;
    color: #006db6;
    margin-top: 0.1rem;
    margin-left: 0.4rem;
  }
}
</style>
